import {OptionModel} from 'models/configuration/OptionModel'
import {TrackKey, TrackModel} from 'models/configuration/TrackModel'
import {EventModel} from 'models/EventModel'
import {RegistrationModel} from 'models/registration/RegistrationModel'
import {Memoize} from 'util/memoize'

export type PackKey = string

export type PackConfig = {
	key: PackKey
	label: string
	description: string
	description_above: string
	preselect: boolean
	show_large: boolean
	has_payment: boolean
	price: number
	has_discount: boolean
	original_price: number
	events: Array<TrackKey>
	disabled: boolean
}

export class PackModel {
	event: EventModel
	config: PackConfig

	constructor(event: EventModel, config: PackConfig) {
		this.event = event
		this.config = config
	}

	@Memoize()
	get isOnline(): boolean {
		return this.key === 'online'
	}

	@Memoize()
	get isPreselected(): boolean {
		if (this.config.preselect) return true
		if (this.event.packs.length === 1) return true
		return false
	}

	@Memoize()
	get isLarge(): boolean {
		return this.config.show_large
	}

	@Memoize()
	get key(): PackKey {
		return this.config.key
	}

	@Memoize()
	get tracks(): Array<TrackModel> {
		const trackKeys = new Set(this.config.events)
		return this.event.tracks.filter(track => trackKeys.has(track.key))
	}

	@Memoize()
	get tracksSet(): Set<TrackModel> {
		return new Set(this.tracks)
	}

	@Memoize()
	get label(): string {
		return this.config.label
	}

	@Memoize()
	get description(): string {
		return this.config.description
	}

	@Memoize()
	get descriptionAbove(): string {
		return this.config.description_above
	}

	@Memoize()
	get originalPrice(): number | false {
		if (!this.config.has_discount) return false
		return this.config.original_price
	}

	@Memoize()
	get disabled(): boolean {
		return this.config.disabled
	}

	@Memoize()
	get tagsSet(): Set<string> {
		return new Set(
			this.tracks.map(track => track.config.tag).filter(tag => !!tag)
		)
	}

	getUniqueSelectedOptions(
		registration: RegistrationModel
	): Array<OptionModel> {
		return registration.options.filter(option => {
			const choice = option.getChoiceFor(registration)
			return (
				choice &&
				option.tracks.length === 1 &&
				this.tracksSet.has(option.tracks[0])
			)
		})
	}

	getPriceFor(registration: RegistrationModel): number | false {
		const status = registration.status
		if (
			status.type === 'invitee' &&
			// Packs are free when all included tracks are free
			this.tracks.every(track => status.free_tracks.includes(track.key))
		)
			return 0
		return this.config.price
	}
}
